import { isCancel as isAxiosCancelError } from 'axios';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { StatusBanner } from '@xing-com/banner';
import { CLASS_NAME_RECENT_CV_BANNERS_WRAPPER } from '@xing-com/crate-jobs-domain-apply-constants';
import type { UserRecentCvData } from '@xing-com/crate-jobs-domain-apply-types';
import type { UploadedFile } from '@xing-com/crate-jobs-domain-apply-uploads';
import { GridItem } from '@xing-com/grid';
import { IconCirclePlus, IconInfo, IconUpload } from '@xing-com/icons';
import { BodyCopy } from '@xing-com/typography';

import { UploadedCvList } from '../../uploaded-cv-list';
import { UploadsList } from '../../uploads-list';
import type { TrackNwtEventType } from '../types';

import * as S from './documents-upload.styles';

type TrackingOrigin = 'cv' | 'other';

type Props = {
  buttonRef: (instance: unknown) => void;
  onUploadFile: (file: File) => Promise<void>;
  onDeleteUpload: (upload: UploadedFile) => Promise<void>;
  trackingOrigin: TrackingOrigin;
  uploads: UploadedFile[];
  maxFiles: number;
  trackNwtEvent: TrackNwtEventType;
  isReduced?: boolean;
  showUploadInProgressError?: boolean;
  resetShowUploadInProgressError?: () => void;
  showAdditionalDocs?: boolean;
  isRequired?: boolean;
  recentCvData?: UserRecentCvData;
  updateUploads?: (updatedUploads: string) => void;
  showRequiredUploadError?: boolean;
};

type UploadError = keyof typeof UPLOAD_ERRORS | null;

export const DEFAULT_MAX_FILESIZE_BYTES = 5242880; // 5MB
export const DEFAULT_ACCEPTED_MIME_TYPES =
  'text/plain,application/msword,application/pdf,image/jpeg,image/png,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.oasis.opendocument.text,application/rtf,text/rtf';
export const UPLOAD_ERRORS = {
  GENERIC: 'JOBS_INSTANT_APPLY_UPLOAD_GENERIC_ERROR',
  MAX_FILES_ALLOWED: 'JOBS_INSTANT_APPLY_UPLOAD_MAX_X_FILES_ALLOWED_ERROR',
  MIME_TYPE_NOT_SUPPORTED:
    'JOBS_INSTANT_APPLY_UPLOAD_MIME_TYPE_NOT_SUPPORTED_ERROR',
  FILE_SIZE_TOO_LARGE: 'JOBS_INSTANT_APPLY_UPLOAD_FILE_SIZE_TOO_LARGE_ERROR',
  UPLOAD_IN_PROGRESS: 'JOBS_INSTANT_APPLY_UPLOAD_IN_PROGRESS_ERROR',
  DELETE: 'JOBS_INSTANT_APPLY_DELETE_UPLOAD_ERROR',
};

const getNwtTrackingOrigin = (origin: TrackingOrigin): string =>
  origin === 'other' ? 'other_files' : origin;

export const DocumentsUpload: React.FC<Props> = React.memo(
  ({
    buttonRef,
    onUploadFile,
    onDeleteUpload,
    showUploadInProgressError = false,
    resetShowUploadInProgressError,
    trackingOrigin,
    uploads,
    maxFiles,
    isReduced = false,
    showAdditionalDocs = true,
    isRequired = false,
    recentCvData,
    updateUploads,
    showRequiredUploadError = false,
    trackNwtEvent,
  }) => {
    const uploadFileRef = useRef<HTMLInputElement>(null);
    const [uploadError, setUploadError] = useState<UploadError>(null);
    const hasRecentCvData =
      recentCvData?.userRecentCvs.length &&
      !recentCvData?.isGetUserRecentCvsError &&
      updateUploads;
    const hasNewUploadedCv = uploads.length > 0;

    useEffect(() => {
      if (showUploadInProgressError) setUploadError('UPLOAD_IN_PROGRESS');
    }, [showUploadInProgressError]);

    const handleDeleteUpload = useCallback(
      async (upload: UploadedFile): Promise<void> => {
        if (uploadFileRef.current) uploadFileRef.current.value = '';
        trackNwtEvent({
          eventSchema: 'basic',
          event: 'deleted',
          element: `saved_${getNwtTrackingOrigin(trackingOrigin)}`,
          page: 'jobs/apply',
          context: 'instant_apply',
        });
        onDeleteUpload(upload).catch(() => setUploadError('DELETE'));
      },
      [onDeleteUpload, trackingOrigin, trackNwtEvent]
    );

    const handleUploadFile = useCallback(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (event: { target: { files: any; value: string } }): void => {
        trackNwtEvent({
          eventSchema: 'basic',
          event: 'uploaded',
          element: getNwtTrackingOrigin(trackingOrigin),
          page: 'jobs/apply',
          context: 'instant_apply',
        });
        const files = [...event.target.files];

        if (uploads.length + files.length > maxFiles) {
          setUploadError('MAX_FILES_ALLOWED');
          trackNwtEvent({
            eventSchema: 'basic',
            event: 'error_shown',
            page: 'jobs/apply',
            context: `instant_apply_${getNwtTrackingOrigin(trackingOrigin)}_upload_error_max_file_number_exceeded`,
          });
        } else {
          files.forEach((file) => {
            if (file.size > DEFAULT_MAX_FILESIZE_BYTES) {
              trackNwtEvent({
                eventSchema: 'basic',
                event: 'error_shown',
                page: 'jobs/apply',
                context: `instant_apply_${getNwtTrackingOrigin(trackingOrigin)}_upload_error_file_size_exceeded`,
              });
              setUploadError('FILE_SIZE_TOO_LARGE');
              return;
            } else if (
              !file.type ||
              !DEFAULT_ACCEPTED_MIME_TYPES.includes(file.type)
            ) {
              trackNwtEvent({
                eventSchema: 'basic',
                event: 'error_shown',
                page: 'jobs/apply',
                context: `instant_apply_${getNwtTrackingOrigin(trackingOrigin)}_upload_error_invalid_file_type`,
              });
              setUploadError('MIME_TYPE_NOT_SUPPORTED');
              return;
            }

            onUploadFile(file)
              .then(() => setUploadError(null))
              .catch((error) => {
                if (!isAxiosCancelError(error)) {
                  trackNwtEvent({
                    eventSchema: 'basic',
                    event: 'error_shown',
                    page: 'jobs/apply',
                    context: `instant_apply_${getNwtTrackingOrigin(trackingOrigin)}_upload_error_network`,
                  });
                  setUploadError('GENERIC');
                }
              });
          });
        }

        event.target.value = '';
      },
      [maxFiles, onUploadFile, trackNwtEvent, trackingOrigin, uploads.length]
    );

    const handleUploadButtonClick = useCallback((): void => {
      if (uploadFileRef.current) {
        uploadFileRef.current.click();
        trackNwtEvent({
          eventSchema: 'basic',
          event: 'clicked',
          element: `upload_${getNwtTrackingOrigin(trackingOrigin)}`,
          page: 'jobs/apply',
          context: 'instant_apply',
        });
      }
    }, [trackNwtEvent, trackingOrigin]);

    const handleErrorBannerClose = useCallback((): void => {
      if (uploadError === 'UPLOAD_IN_PROGRESS') {
        resetShowUploadInProgressError?.();
      }
      setUploadError(null);
    }, [resetShowUploadInProgressError, uploadError]);

    const NewUploadsList = hasNewUploadedCv ? (
      <S.UploadsListContainer $isReduced={isReduced}>
        <UploadsList uploads={uploads} onDeleteUpload={handleDeleteUpload} />
      </S.UploadsListContainer>
    ) : null;

    return (
      <>
        {isReduced ? (
          <S.ReducedUploadButton
            icon={IconCirclePlus}
            size="medium"
            data-testid="reduced-upload-button"
            disabled={uploads.length >= maxFiles}
            onClick={handleUploadButtonClick}
            ref={buttonRef}
          >
            <FormattedMessage id="JOBS_INSTANT_APPLY_UPLOAD_OTHER_BUTTON" />
          </S.ReducedUploadButton>
        ) : (
          <S.GridContainer>
            <GridItem span={12} spanMedium={7}>
              {showAdditionalDocs ? (
                <BodyCopy size="small" noMargin>
                  <FormattedMessage id="JOBS_INSTANT_APPLY_UPLOAD_HINT_FILES" />
                </BodyCopy>
              ) : null}
              <S.Meta size="xsmall" noMargin>
                <IconInfo width={16} height={16} aria-hidden />
                <FormattedMessage id="JOBS_INSTANT_APPLY_UPLOAD_HINT_FORMAT_SHORT" />
              </S.Meta>
            </GridItem>
            <GridItem span={12} spanMedium={5}>
              <S.UploadButton
                disabled={uploads.length >= maxFiles}
                icon={IconUpload}
                size="medium"
                variant="tertiary"
                data-testid="upload-button"
                onClick={handleUploadButtonClick}
                ref={buttonRef}
              >
                <FormattedMessage id="JOBS_INSTANT_APPLY_UPLOAD_CV_BUTTON" />
              </S.UploadButton>
            </GridItem>
          </S.GridContainer>
        )}
        <input
          hidden
          multiple={maxFiles > 1}
          type="file"
          accept={DEFAULT_ACCEPTED_MIME_TYPES}
          name="fileToUpload"
          ref={uploadFileRef}
          onChange={handleUploadFile}
          data-testid={`upload-input-${trackingOrigin}`}
        />
        {uploadError && (
          <S.ErrorBannerWrapper $isReduced={isReduced}>
            <StatusBanner
              aria-live="polite"
              variant="error"
              display="inline"
              handleOnClose={handleErrorBannerClose}
              data-testid="upload-error-banner"
            >
              <FormattedMessage
                id={UPLOAD_ERRORS[uploadError]}
                values={{ maxFiles }}
              />
            </StatusBanner>
          </S.ErrorBannerWrapper>
        )}
        <S.AriaMessage aria-live="polite">
          {uploads.map((upload) => (
            <FormattedMessage
              key={upload.id}
              id="JOBS_INSTANT_APPLY_UPLOAD_FILE_SUCCESS_A11Y"
              values={{ fileName: upload.fileName }}
            />
          ))}
        </S.AriaMessage>
        {hasRecentCvData ? (
          <S.UploadsListContainer
            $isReduced={false}
            className={CLASS_NAME_RECENT_CV_BANNERS_WRAPPER}
          >
            <UploadedCvList
              uploads={uploads}
              onDeleteUpload={handleDeleteUpload}
              isRequiredCv={isRequired}
              updateFormUploadId={updateUploads}
              userRecentCvs={recentCvData.userRecentCvs}
              trackNwtEvent={trackNwtEvent}
            />
          </S.UploadsListContainer>
        ) : (
          NewUploadsList
        )}

        <S.UploadDocsBannerWrapper
          $hasPreviousErrorBanner={!!uploadError}
          aria-live="polite"
        >
          <StatusBanner
            aria-live="polite"
            display="inline"
            variant="error"
            show={showRequiredUploadError}
          >
            <FormattedMessage id="JOBS_INSTANT_APPLY_UPLOAD_DOCUMENTS_FIELD_ERROR" />
          </StatusBanner>
        </S.UploadDocsBannerWrapper>
      </>
    );
  }
);
